import React, { useState } from 'react';
import Button from '../buttons/baseButton';
import customIcons from '../../utils/icons';
import InformationText from '../InformationText';
import useRequestState from '../../hooks/useRequestState';
import sanitize from '../../utils/formatters';
import PostAddAnalysisCohort from '../../services/adapters/addAnalysisCohort';
import { AnalysisCohort } from '../../api-models';
import { CreateCohortFormData } from '../../types/forms.type';
import Chip from '../chip';
import CohortSummary from './cohortSummary';

export default function CreateCohortForm({ snapshots, households, onClose }: { snapshots: { start: string, end: string }, households: string[], onClose: () => void }) {
  const [title, setTitle] = useState('');
  const [warning, setWarning] = useState('');
  const [selectedCampaignType, setSelectedCampaignType] = useState<string>('');
  const [selectedCommunicationType, setSelectedCommunicationType] = useState<string>('');
  const hasSelectedFilters = Object.keys(households).length > 0;

  const filterSummaryComponent = {
    'Household references': households.map((hr) => <Chip key={hr} text={hr} colour="blue" small closeButton={false} />),
    Campaign: selectedCampaignType ? <Chip key={selectedCampaignType} text={selectedCampaignType} colour="red" small closeButton={false} /> : null,
    Communication: selectedCommunicationType ? <Chip key={selectedCommunicationType} text={selectedCommunicationType} colour="yellow" small closeButton={false} /> : null,
    Snapshots: <div className="text-sm">{`${snapshots.start} → ${snapshots.end}`}</div>,
  };
  const {
    error: createCohortError,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<AnalysisCohort>();

  const createAnalysisCohort = async () => {
    const sanitizedTitle = sanitize(title);
    const addCohortRequest: CreateCohortFormData = {
      name: sanitizedTitle,
      selectedHouseholds: households,
      startSnapshot: snapshots.start,
      endSnapshot: snapshots.end,
      tags: [selectedCampaignType, selectedCommunicationType],
    };
    const addCohortAdapter = new PostAddAnalysisCohort();
    const response = addCohortAdapter.adaptResponse(addCohortRequest);
    await handleRequest(response);
    if (!createCohortError) {
      onClose();
    }
  };
  const campaignTypeOptions = ['Pension Credit',
    'Attendance Allowance',
    'Healthy Start',
    'Free School Meals',
    'Support for Families',
    'Support for Pensioners',
    'Homelessness',
    'Employment',
    'Debt',
    'Discretionary Support',
    'Other'];
  const communicationTypeOptions = ['Letter',
    'Text',
    'Phone Calls',
    'Multi Channel Communication'];
  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue.length >= 75) {
      setWarning('cohort name cannot exceed 75 characters.');
    } else {
      setWarning('');
      setTitle(inputValue);
    }
  };

  const errorMessage = `0 valid households selected.
  Please input and check households to create a cohort.`;

  const formError = (
    <div className="flex flex-col items-center justify-center gap-4 p-4 h-full">
      <span className="text-2xl text-pipstatus-alert">{customIcons.errorIcon}</span>
      <span className="text-md text-piptext-body whitespace-pre-line">
        {errorMessage || createCohortError?.message }
      </span>
    </div>
  );
  const cohortForm = (
    <div className="flex flex-col gap-2">
      <div id="modal-header" className="flex items-center text-start gap-2 w-full">
        <i className="mx-4 text-2xl text-primary-vibrant">{customIcons.save}</i>
        <InformationText title="Create Cohort" subtitle="" />
      </div>
      <div className="w-full overflow-y-scroll gap-4 bg-white p-1">
        <label htmlFor="analysis-cohort-name" className="flex flex-col text-start m-4">
          <p className="input-label">Cohort name</p>
          <input
            key="analysis-cohort-name"
            id="analysis-cohort-name"
            type="text"
            maxLength={75}
            name="analysisCohortName"
            placeholder="eg. Q3 Benefits Check"
            className="input-default focus:input-focused"
            value={title}
            onChange={handleTextInputChange}
          />
          {warning && <span className="text-pipstatus-alert">{warning}</span>}
        </label>
        <div className="flex flex-col justify-center items-start leading-7 m-4">
          <p className="input-label">Campaign type</p>
          <p className="text-xs text-pipgrey">select the type of campaign this cohort belongs to.</p>
        </div>
        <div className="m-4 flex flex-wrap gap-2">
          {campaignTypeOptions.map((option) => (
            <Chip
              key={option}
              text={option}
              closeButton={false}
              colour={selectedCampaignType === option ? 'red' : 'default'}
              onClick={() => setSelectedCampaignType(option)}
            />
          ))}
        </div>

        <div className="flex flex-col justify-center items-start leading-7 m-4">
          <p className="input-label">Communication type</p>
          <p className="text-xs text-pipgrey">How was the cohort contacted?</p>
        </div>
        <div className="m-4 flex flex-wrap gap-2">
          {communicationTypeOptions.map((option) => (
            <Chip
              key={option}
              text={option}
              closeButton={false}
              colour={selectedCommunicationType === option ? 'yellow' : 'default'}
              onClick={() => setSelectedCommunicationType(option)}
            />
          ))}
        </div>
        <CohortSummary summaryTitle="Summary" filterSummaryComponent={filterSummaryComponent} />
      </div>

      <div className="flex justify-end w-full">
        <FallbackWrapper loadingMessage="Creating cohort...">
          <div className="flex gap-4 ">
            <Button
              id="cancel-modal-button"
              label="Cancel"
              className="button-default "
              onClick={onClose}
            />
            <Button
              id="submit-cohort-modal-button"
              label="Save Preset"
              className="button-green"
              onClick={createAnalysisCohort}
              icon={customIcons.save}
              isDisabled={!title || !snapshots.start || !snapshots.end}
            />
          </div>
        </FallbackWrapper>
      </div>
    </div>
  );

  return (hasSelectedFilters ? cohortForm : (formError));
}
